import React, { KeyboardEvent, useEffect, useState } from "react";
import { FormikFieldProps } from "../formUtils";
import { AutocompleteOption, AutocompleteWrapper } from "../muiAutocomplete";
import { useTranslation } from "react-i18next";
import { getRoutes, getRoute } from "../../../services/routeService";

interface RouteSelectProps extends Partial<FormikFieldProps> {
    id?: string;
    required?: boolean;
    multiple?: boolean;
    small?: boolean;
    open?: boolean;
    defaultOpen?: boolean;
    label?: string;
    autoFocus?: boolean;
    disabled?: boolean;
    onChange?: (id: string | string[] | null, title?: string | null) => void;
    onKeyDown?: (e: KeyboardEvent) => void;
    onCreateRoute?: () => void;
}

const CREATE_ID = "__create_route__";

export const RouteSelect = (props: RouteSelectProps) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [options, setOptions] = useState<AutocompleteOption[]>([]);
    const {
        id,
        value,
        small,
        onChange,
        onBlur,
        onKeyDown,
        multiple,
        open,
        autoFocus,
        defaultOpen,
        onCreateRoute,
        ...rest
    } = props;

    useEffect(() => {
        let mounted = true;
        setLoading(true);

        getRoutes()
            .then(async(res) => {
                if (!mounted) return;
                const inactiveSuffix = t("route.inactiveSuffix");
                const base = (res || []).map((r) => ({
                    id: r?.id,
                    title: r?.name ? `${r.name}${r?.isActive === false ? inactiveSuffix : ""}` : "",
                })) as AutocompleteOption[];

                // Ensure currently selected value(s) are present even if inactive
                const ensureSelectedPresent = async(): Promise<AutocompleteOption[]> => {
                    const current = Array.isArray(value) ? value : (value ? [value] : []);
                    const missingIds = current.filter((id) => !!id && !base.some((o) => o.id === id));
                    if (missingIds.length === 0) return base;

                    const fetched: AutocompleteOption[] = [];
                    for (const id of missingIds) {
                        try {
                            const r = await getRoute(id);
                            if (r?.id) {
                                fetched.push({
                                    id: r.id,
                                    title: r.name ? `${r.name}${r?.isActive === false ? inactiveSuffix : ""}` : "",
                                });
                            }
                        } catch (e) { }
                    }
                    return [...base, ...fetched];
                };

                const merged = await ensureSelectedPresent();

                const createOption: AutocompleteOption = {
                    id: CREATE_ID,
                    title: t("route.newRoute"),
                    extra: "create",
                };

                const withCreate = !multiple && onCreateRoute ? [createOption, ...merged] : merged;
                setOptions(withCreate);
            })
            .catch(console.error)
            .finally(() => mounted && setLoading(false));

        return () => {
            mounted = false;
        };
    }, [multiple, onCreateRoute, t, value]);

    const handleChange = (val: AutocompleteOption | AutocompleteOption[] | null) => {
        if (!onChange) return;

        if (Array.isArray(val)) {
            const hasCreate = val.some((v) => v.id === CREATE_ID);
            if (hasCreate) {
                onCreateRoute?.();
            }
            const filtered = val.filter((v) => v.id !== CREATE_ID);
            onChange(filtered.map((v) => v.id));
            return;
        }

        if (val?.id === CREATE_ID) {
            onCreateRoute?.();
            return;
        }

        onChange(val?.id ?? null, val?.title ?? null);
    };

    return (
        <AutocompleteWrapper
            id={id}
            label={props.label || t("route.route")}
            value={value ?? ""}
            size={small ? "small" : "medium"}
            options={options}
            onChange={handleChange}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            loading={loading}
            required={props.required}
            multiple={multiple}
            open={open}
            autoFocus={autoFocus}
            textFieldProps={{ ...rest }}
            disabled={props.disabled}
            defaultOpen={defaultOpen}
        />
    );
};